import { FormControl, NativeSelect, Tooltip } from '@mui/material';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import ForbiddenTooltip from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import React, { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { AccessLevelModel } from 'shared/domain/issue/mapping/accessLevel/model/types';
import { UserRole } from 'shared/types/userRole';
import { accessLevelModelToValueWithLabel } from '../../../../shared/domain/issue/mapping/accessLevel/toOnView/accessLevelModelToValueWithLabel';
import { useStyles } from './style';
import {
  ManagementSelectOptionsProps,
  ManagementSelectProps,
} from './types';

const AVAILABLE_OPTIONS: Record<UserRole, AccessLevelModel[]> = {
  [UserRole.superAdmin]: [AccessLevelModel.EDIT],
  [UserRole.organizationAdmin]: [AccessLevelModel.EDIT],
  [UserRole.projectAdmin]: [AccessLevelModel.EDIT],
  [UserRole.manager]: [AccessLevelModel.EDIT],
  [UserRole.viewer]: [AccessLevelModel.EDIT, AccessLevelModel.READ],
  [UserRole.standard]: [
    AccessLevelModel.EDIT,
    AccessLevelModel.READ,
    AccessLevelModel.NONE,
  ],
  [UserRole.visible_no_access]: [AccessLevelModel.NONE],
};
const options = Object.values(AccessLevelModel).map((accessLevel) =>
  accessLevelModelToValueWithLabel(accessLevel)
);
const disabledStyle = {
  color: '#D8D8D8',
};

function MicroManagementSelectOptions({
  accessLevelObject,
  disabled,
}: ManagementSelectOptionsProps): ReactElement {
  const intl = useIntl();
  return (
    <option
      value={accessLevelObject.value}
      disabled={disabled}
      style={disabled ? disabledStyle : undefined}
    >
      {formatTranslation(intl, unsafeTranslationKey(accessLevelObject.label),)}
    </option>
  );
}

export function ManagementSelect({
  currentAccess,
  onChange,
  userId,
  userLabel,
  userRole,
  disabled,
  disabledReasons,
  explanationLabel,
  Icon,
}: ManagementSelectProps): ReactElement {
  const classes = useStyles();
  const intl = useIntl();
  const select = (
    <FormControl
      data-qa={'form-control-accessManagementSelect'}
      variant='standard'
      className={classes.permissionSelect}
      sx={{ px: 1 }}
    >
      <div>
        {Icon}
        <NativeSelect
          disableUnderline
          sx={{ borderRadius: 6 }}
          disabled={disabled}
          defaultValue={currentAccess}
          inputProps={{
            name: 'access_type',
            id: 'uncontrolled-native-access',
          }}
          onChange={(
            event: React.ChangeEvent<HTMLSelectElement>
          ): void => {
            onChange(
              event.target.value as AccessLevelModel,
              currentAccess,
              userId,
              userLabel
            );
          }}
        >
          {options.map((accessLevelObject) => (
            <MicroManagementSelectOptions
              key={accessLevelObject.value}
              accessLevelObject={accessLevelObject}
              disabled={isOptionDisabled(accessLevelObject, userRole)}
            />
          ))}
        </NativeSelect>
      </div>
    </FormControl>
  );

  if (disabled) {
    return (
      <ForbiddenTooltip visibleWhen={disabled} reasons={disabledReasons}>
        {select}
      </ForbiddenTooltip>
    );
  }

  return (
    <Tooltip title={formatTranslation(intl, unsafeTranslationKey(explanationLabel) )}>
      {select}
    </Tooltip>
  );
}

function isOptionDisabled(
  accessLevelObject: { value: AccessLevelModel; label: string },
  userRole: UserRole
): boolean {
  return !AVAILABLE_OPTIONS[userRole].includes(accessLevelObject.value);
}
